import { IconEdit, IconSave, IconTrash } from '@/icons';
import { Button } from '@/ui/Button/Button';
import { Flex } from '@/ui/Flex/Flex';
import { Input } from '@/ui/Input/Input';
import { Modal } from '@/ui/Modal/Modal';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { envIsElectron } from 'src/const/envCheck';
import { SensorType } from '@/types/SensorType';
import { MagicModel } from '@/types/MagicModel';
import { Tooltip } from '@/ui/Tooltip/Tooltip';
import { TOASTTYPE, useToast } from '@/ui/Toast/Toast';
import styled, { css } from 'styled-components';
import CustomSelect from '@/ui/Select/Select';
import { TriggerOption } from './SensorMappingSidebar/SensorMappingSidebar';

interface Props {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setMagicModels: React.Dispatch<React.SetStateAction<MagicModel[]>>;
  magicModels: MagicModel[];
}

const StyledSaveButton = styled(Button)`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  align-self: flex-end;
  cursor: pointer;

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme: currentTheme }) =>
        currentTheme.color('#B2B1BA')};
      pointer-events: none;
    `}
`;

export const SensorLibraryModal = ({
  magicModels,
  setMagicModels,
  setIsModalOpen,
}: Props) => {
  const toast = useToast();
  const intl = useIntl();
  const [sensorType, setSensorType] = useState<null | SensorType>(
    SensorType.NEXMO_NFC,
  );
  const [sensorId, setSensorId] = useState('');
  const [modelName, setModelName] = useState('');
  const [editedSensorType, setEditedSensorType] = useState<null | SensorType>(
    null,
  );
  const [editedModelName, setEditedModelName] = useState('');
  const [editedSensorId, setEditedSensorId] = useState('');
  const [editMode, setEditMode] = useState<null | string>(null);
  const MMListRef = useRef<null | HTMLElement>(null);

  const options: TriggerOption[] = [
    {
      value: SensorType.NEXMO_NFC,
      label: intl.formatMessage({ defaultMessage: 'NFC Reader', id: '1rhYzD' }),
      inputType: 'textfield',
    },
    {
      value: SensorType.TH_PUSH,
      label: intl.formatMessage({
        defaultMessage: 'Trihow Push',
        id: 'tnVnMQ',
      }),
      inputType: 'input',
    },
  ];
  useEffect(() => {
    MMListRef.current = document.querySelector('.magic-model-list');
  }, []);

  const mockedAvailableTags = ['040F86EA6A5B80', '04DD0BEA6A5B84'];
  const [mockedPlacedTags, setMockedPlacedTags] = useState<string[]>([]);

  const clearNewModelValues = () => {
    setModelName('');
    setSensorId('');
    setSensorType(options[0].value);
  };
  const scrollToBottom = () => {
    if (MMListRef.current != null && MMListRef.current.lastElementChild) {
      MMListRef.current.lastElementChild.scrollIntoView();
    }
  };
  const saveMagicModels = () => {
    setEditMode(null);
    if (
      magicModels.find(
        (MM) => MM.sensorId === sensorId || MM.modelName === modelName,
      ) ||
      sensorId === '' ||
      modelName === ''
    ) {
      toast({
        subtitle: intl.formatMessage({
          defaultMessage: 'Sensor or name already in use!',
          id: 'xeOI2g',
        }),
        mode: TOASTTYPE.Warn,
      });
      return;
    }
    setMagicModels((currentMagicModels: MagicModel[]) => {
      const definiteSensorType = sensorType || SensorType.EMPTY;
      const updatedTags = [
        ...currentMagicModels,
        { sensorType: definiteSensorType, sensorId, modelName },
      ];
      return updatedTags;
    });
    clearNewModelValues();
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, 0);
    return () => {
      clearTimeout(timeoutId);
    };
  };

  const deleteMagicModels = (currentSensorId: string) => {
    setMagicModels((prev) => {
      return prev.filter((MM) => MM.sensorId !== currentSensorId);
    });
  };

  const mapSensorId = (tagData: unknown) => {
    const castedTagData = tagData as {
      value: { tagId: string; channel: string };
    };
    if (!editMode) {
      setSensorId(castedTagData.value.tagId);
    } else {
      setEditedSensorId(castedTagData.value.tagId);
    }
  };

  useEffect(() => {
    if (!envIsElectron || process.env.MOCKED_READER === 'false') return;

    const interval = setInterval(() => {
      window.electron.sendMockedReaderScanResponse(mockedPlacedTags);
    }, 200);

    return () => clearInterval(interval);
  }, [mockedPlacedTags]);

  useEffect(() => {
    if (!envIsElectron) {
      console.warn(
        "Emmitting tagId is available only in Electron's environment",
      );
      return;
    }

    const emitTagEvent = window.electron.ipcRenderer.on(
      'emitTagId',
      mapSensorId,
    );

    return () => {
      emitTagEvent();
    };
  }, [editMode]);

  const modifyMagicModel = (
    magicModel: MagicModel,
    isMMInEditMode: boolean,
  ) => {
    if (isMMInEditMode) {
      if (
        editedModelName === '' ||
        editedSensorId === '' ||
        editedSensorType === SensorType.EMPTY
      ) {
        setEditMode(null);
        return;
      }
      setMagicModels((currentMagicModels: MagicModel[]) => {
        const definiteSensorType = editedSensorType || SensorType.EMPTY;
        const updatedMagicModels = currentMagicModels.map((MM) => {
          if (MM.sensorId === magicModel.sensorId) {
            return {
              ...MM,
              sensorId: editedSensorId,
              sensorType: definiteSensorType,
              modelName: editedModelName,
            };
          }
          return MM;
        });
        return updatedMagicModels;
      });
      setEditMode(null);
    } else {
      setEditedModelName(magicModel.modelName);
      setEditedSensorId(magicModel.sensorId);
      setEditedSensorType(magicModel.sensorType);
      setEditMode(magicModel.sensorId);
    }
  };

  return (
    <Modal
      styles={css`
        max-width: 750px;
        min-width: 750px;
      `}
      contentStyles={css`
        min-height: 300px;
        max-height: 600px;
        overflow-x: auto;
      `}
      onClose={() => {
        setEditMode(null);
        setIsModalOpen(false);
      }}
      title="Sensor Library"
    >
      {process.env.NODE_ENV !== 'production' && envIsElectron && (
        <Flex gap={16} padding={16} justify="center">
          {mockedAvailableTags.map((tag) => (
            <Button
              key={tag}
              onClick={() => {
                setMockedPlacedTags((prev) => {
                  return prev.includes(tag) ? [] : [tag];
                });
              }}
            >
              {tag}
            </Button>
          ))}
        </Flex>
      )}

      <Flex
        gap={16}
        direction="column"
        justify="center"
        padding="0px 16px"
        className="magic-model-list"
      >
        {magicModels.map((magicModel) => {
          const isMMInEditMode = magicModel.sensorId === editMode;

          return (
            <Flex justify="space-between" key={magicModel.sensorId}>
              <Flex gap={32}>
                <CustomSelect
                  size="medium"
                  disabled={!isMMInEditMode}
                  isSearchable={false}
                  onChange={(newValue) => {
                    if (!newValue) return;
                    setEditedSensorType(newValue.value);
                  }}
                  options={options}
                  placeholder="Sensor Type"
                  value={
                    // eslint-disable-next-line no-nested-ternary
                    isMMInEditMode
                      ? options.find(({ value }) => editedSensorType === value)
                      : (options.find(
                          ({ value }) => magicModel.sensorType === value,
                        ) ?? options[0])
                  }
                />

                <Input
                  disabled={!isMMInEditMode}
                  placeholder="Sensor ID"
                  value={
                    // eslint-disable-next-line no-nested-ternary
                    isMMInEditMode ? editedSensorId : magicModel.sensorId
                  }
                  onChange={(value) => {
                    setEditedSensorId(value);
                  }}
                />
                <Input
                  disabled={!isMMInEditMode}
                  placeholder="Magic Model Name"
                  value={
                    // eslint-disable-next-line no-nested-ternary
                    isMMInEditMode ? editedModelName : magicModel.modelName
                  }
                  onChange={(value) => setEditedModelName(value)}
                />
              </Flex>
              <Flex gap={32} align="center">
                <Tooltip
                  title={isMMInEditMode ? 'Apply changes' : 'Edit Sensor'}
                >
                  <button
                    type="button"
                    style={{
                      transform: 'scale(1.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                    onClick={() => modifyMagicModel(magicModel, isMMInEditMode)}
                  >
                    {isMMInEditMode ? <IconSave /> : <IconEdit />}
                  </button>
                </Tooltip>
                <Tooltip title="Delete sensor">
                  <button
                    type="button"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      deleteMagicModels(magicModel.sensorId);
                    }}
                  >
                    <IconTrash />
                  </button>
                </Tooltip>
              </Flex>
            </Flex>
          );
        })}
        <Flex gap={16} justify="space-between">
          <Flex gap={32}>
            <CustomSelect
              size="medium"
              isSearchable={false}
              onChange={(newValue) => {
                if (!newValue) return;
                setSensorType(newValue.value);
              }}
              options={options}
              placeholder="Sensor Type"
              value={
                options.find(({ value }) => sensorType === value) ?? options[0]
              }
            />
            <Input
              placeholder="Sensor ID"
              value={sensorId}
              onChange={(value) => {
                setSensorId(value);
              }}
            />
            <Input
              placeholder="Magic Model Name"
              value={modelName}
              onChange={(value) => setModelName(value)}
            />
          </Flex>
          <Flex>
            <StyledSaveButton
              disabled={!(sensorId && modelName)}
              onClick={saveMagicModels}
            >
              <FormattedMessage defaultMessage="Add" id="2/2yg+" />
            </StyledSaveButton>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  );
};
